import { useIntl, defineMessages } from 'react-intl'
import { Box, Flex, Progress, useTheme } from '@homebotapp/hb-react-component-catalog'
import { BucketedMonthlyHomeExpenses, ListingLoanInfo } from '../../../../../api/gqlaxy/gql/generated/graphql'
import { EstimatedMonthlyPaymentBreakdownHoaFees } from './EstimatedMonthlyPaymentBreakdownHoaFees/EstimatedMonthlyPaymentBreakdownHoaFees'
import { EstimatedMonthlyPaymentBreakdownCustomerDM } from './EstimatedMonthlyPaymentBreakdownCustomerDM/EstimatedMonthlyPaymentBreakdownCustomerDM'
import { EstimatedMonthlyPaymentBreakdownPropertyTaxes } from './EstimatedMonthlyPaymentBreakdownProperyTaxes/EstimatedMonthlyPaymentBreakdownPropertyTaxes'
import { EstimatedMonthlyPaymentBreakdownHomeInsurance } from './EstimatedMonthlyPaymentBreakdownHomeInsurance/EstimatedMonthlyPaymentBreakdownHomeInsurance'
import { EstimatedMonthlyPaymentBreakdownMortgageInsurance } from './EstimatedMonthlyPaymentBreakdownMortgageInsurance/EstimatedMonthlyPaymentBreakdownMortgageInsurance'
import { EstimatedMonthlyPaymentBreakdownPrincipalAndInterest } from './EstimatedMonthlyPaymentBreakdownPrincipalAndInterest/EstimatedMonthlyPaymentBreakdownPrincipalAndInterest'
import { EstimatedMonthlyPaymentBreakdownNationalAvgDownPaymentCard } from './EstimatedMonthlyPaymentBreakdownNationalAvgDownPaymentCard/EstimatedMonthlyPaymentBreakdownNationalAvgDownPaymentCard'
import { EstimatedMonthlyPaymentBreakdownDonutChart } from './EstimatedMonthlyPaymentBreakdownDonutChart/EstimatedMonthlyPaymentBreakdownDonutChart'

export const MSG = defineMessages({
  breakdown: {
    id: 'EstimatedMonthlyPaymentBreakdown.breakdown',
    defaultMessage: 'Estimated monthly payment breakdown'
  }
})

export const NATIONAL_AVG_DOWN_PAYMENT = 0.07

export interface EstimatedMonthlyPaymentBreakdownProps {
  address?: string
  homePrice: number
  listingId: string
  loanInfo?: ListingLoanInfo | null
  setDownPaymentAmountDollars: (downPaymentCash: number) => void
  bucketedMonthlyHomeExpenses?: BucketedMonthlyHomeExpenses | null
  assumableBucketedMonthlyHomeExpenses?: BucketedMonthlyHomeExpenses | null
  assumableMortgageLoanInfo?: ListingLoanInfo | null
  assumableMortgageSecondLoanInfo?: ListingLoanInfo | null
  assumableToggle?: boolean
}

export const EstimatedMonthlyPaymentBreakdown = ({
  address,
  loanInfo,
  listingId,
  homePrice,
  setDownPaymentAmountDollars,
  bucketedMonthlyHomeExpenses,
  assumableMortgageSecondLoanInfo,
  assumableBucketedMonthlyHomeExpenses,
  assumableMortgageLoanInfo,
  assumableToggle = false
}: EstimatedMonthlyPaymentBreakdownProps) => {
  const intl = useIntl()
  const { theme } = useTheme()
  const isLight = theme.isLightThemeType

  const selectedBucketedMonthlyHomeExpenses = assumableToggle
    ? assumableBucketedMonthlyHomeExpenses
    : bucketedMonthlyHomeExpenses

  const hoaFees = selectedBucketedMonthlyHomeExpenses?.monthlyHoaFees || 0
  const homeInsurance = selectedBucketedMonthlyHomeExpenses?.homeInsurance || 0
  const propertyTaxes = selectedBucketedMonthlyHomeExpenses?.propertyTaxes || 0
  const mortgageInsurance = selectedBucketedMonthlyHomeExpenses?.mortgageInsurance || 0
  const principalAndInterest = selectedBucketedMonthlyHomeExpenses?.monthlyPrincipalAndInterest || 0
  const secondMortgagePrincipalAndInterest =
    assumableBucketedMonthlyHomeExpenses?.secondMortgagePrincipalAndInterest || 0
  const downPaymentPercentage =
    Number((loanInfo?.downpayment && (loanInfo?.downpayment / homePrice) * 100)?.toFixed(2)) || 0

  const showAvgPayment = downPaymentPercentage === Number((NATIONAL_AVG_DOWN_PAYMENT * 100).toFixed(2))

  return (
    <>
      <EstimatedMonthlyPaymentBreakdownDonutChart
        assumableToggle={assumableToggle}
        hoaFees={hoaFees}
        propertyTaxes={propertyTaxes}
        homeInsurance={homeInsurance}
        mortgageInsurance={mortgageInsurance}
        principalAndInterest={principalAndInterest}
        secondMortgagePrincipalAndInterest={secondMortgagePrincipalAndInterest}
        totalMonthlyHomeExpenses={selectedBucketedMonthlyHomeExpenses?.totalMonthlyHomeExpenses || 0}
      />
      <Flex gap={2} mt={2} direction='column'>
        <Box
          transform={showAvgPayment ? 'scale3d(1,1,1)' : 'scale3d(.8,.8,-10)'}
          height={showAvgPayment ? 'auto' : '0'}
          opacity={showAvgPayment ? '100' : '0'}
          transition='transform .2s ease-in-out, height .2s ease-in-out, opacity .1s ease-in-out'
        >
          <EstimatedMonthlyPaymentBreakdownNationalAvgDownPaymentCard />
        </Box>
        <EstimatedMonthlyPaymentBreakdownPrincipalAndInterest
          loanInfo={loanInfo}
          homePrice={homePrice}
          principalAndInterest={principalAndInterest}
          setDownPaymentAmountDollars={setDownPaymentAmountDollars}
          assumableToggle={assumableToggle}
          assumableBucketedMonthlyHomeExpenses={assumableBucketedMonthlyHomeExpenses}
          assumableMortgageLoanInfo={assumableMortgageLoanInfo}
          assumableMortgageSecondLoanInfo={assumableMortgageSecondLoanInfo}
        />
        <EstimatedMonthlyPaymentBreakdownMortgageInsurance
          mortgageInsurance={mortgageInsurance}
          downPaymentPercentage={downPaymentPercentage}
        />
        <EstimatedMonthlyPaymentBreakdownPropertyTaxes propertyTaxes={propertyTaxes} />
        <EstimatedMonthlyPaymentBreakdownHomeInsurance homeInsurance={homeInsurance} />
        <EstimatedMonthlyPaymentBreakdownHoaFees hoaFees={hoaFees} />
        <EstimatedMonthlyPaymentBreakdownCustomerDM listingId={listingId} address={address} />
      </Flex>
    </>
  )
}
